import type { Ctx } from 'boardgame.io';
import type {
  AoSGameState,
  MoveGoodsArgs
} from '../../types';

/**
 * バリデーション結果
 */
export interface ValidationResult {
  success: boolean;
  error?: string;
  message?: string;
}

/**
 * 商品輸送のバリデーション（基本チェック）
 *
 * @param G - ゲーム状態
 * @param ctx - Boardgame.ioコンテキスト
 * @param args - 商品輸送引数
 * @returns バリデーション結果
 *
 * @see docs/specifications/logic/common/phase_05_move_goods.md Section 3
 */
export function validateMoveGoodsBasic(
  G: AoSGameState,
  ctx: Ctx,
  args: MoveGoodsArgs
): ValidationResult {
  const player = G.players[ctx.currentPlayer];

  // 1. 前提条件チェック
  if (ctx.phase !== 'moveGoods') {
    return {
      success: false,
      error: 'INVALID_PHASE',
      message: `Current phase is ${ctx.phase}, expected moveGoods`
    };
  }

  if (player.isBankrupt) {
    return {
      success: false,
      error: 'PLAYER_BANKRUPT',
      message: 'Bankrupt players cannot move goods'
    };
  }

  // 2. 商品存在チェック（都市、町、または新都市タイル）
  const fromHex = G.map.find(hex =>
    (hex.city?.id === args.fromCityId) ||
    (hex.town?.id === args.fromCityId) ||
    (hex.town?.newCity?.id === args.fromCityId)
  );

  if (!fromHex) {
    return {
      success: false,
      error: 'LOCATION_NOT_FOUND',
      message: `Location ${args.fromCityId} not found`
    };
  }

  const toCityHex = G.map.find(hex =>
    (hex.city?.id === args.toCityId) ||
    (hex.town?.newCity?.id === args.toCityId)
  );

  if (!toCityHex) {
    return {
      success: false,
      error: 'CITY_NOT_FOUND',
      message: `City ${args.toCityId} not found`
    };
  }

  // 商品存在チェック
  if (!fromHex.goods.includes(args.goodsColor)) {
    return {
      success: false,
      error: 'GOODS_NOT_FOUND',
      message: `Goods ${args.goodsColor} not found at ${args.fromCityId}`
    };
  }

  // 目的都市の色チェック（元の都市 or 新都市タイル）
  const toCity = toCityHex.city || toCityHex.town?.newCity;
  if (!toCity) {
    return {
      success: false,
      error: 'DESTINATION_NOT_FOUND',
      message: `City ${args.toCityId} not found`
    };
  }

  // color が配列の場合は複数色を受け入れる
  const acceptedColors = Array.isArray(toCity.color) ? toCity.color : [toCity.color];
  if (!acceptedColors.includes(args.goodsColor)) {
    return {
      success: false,
      error: 'DESTINATION_COLOR_MISMATCH',
      message: `City ${args.toCityId} accepts ${acceptedColors.join(', ')}, but goods color is ${args.goodsColor}`
    };
  }

  return { success: true };
}

/**
 * 機関車強化のバリデーション
 *
 * @param G - ゲーム状態
 * @param ctx - Boardgame.ioコンテキスト
 * @returns バリデーション結果
 *
 * @see docs/specifications/logic/common/phase_05_move_goods.md Section 3.5
 */
export function validateUpgradeLoco(
  G: AoSGameState,
  ctx: Ctx
): ValidationResult {
  const player = G.players[ctx.currentPlayer];

  // 1. 前提条件チェック
  if (ctx.phase !== 'moveGoods') {
    return {
      success: false,
      error: 'INVALID_PHASE',
      message: `Current phase is ${ctx.phase}, expected moveGoods`
    };
  }

  if (player.isBankrupt) {
    return {
      success: false,
      error: 'PLAYER_BANKRUPT',
      message: 'Bankrupt players cannot upgrade locomotive'
    };
  }

  // 2. 今ターン未実施チェック
  if (player.hasUpgradedLocoThisTurn) {
    return {
      success: false,
      error: 'LOCOMOTIVE_ALREADY_UPGRADED',
      message: 'Locomotive already upgraded this turn'
    };
  }

  // 3. 上限チェック
  if (player.locomotiveLevel >= 6) {
    return {
      success: false,
      error: 'MAX_LOCOMOTIVE_LEVEL',
      message: 'Locomotive level is already at maximum (6)'
    };
  }

  // 4. 資金チェック（削除: 機関車強化は無料）
  // 基本ルール: 商品輸送フェーズ内での機関車強化は無料
  // （輸送を行わない代わりのアクション）

  return { success: true };
}
